import React from 'react';
import type { StudioWatermarkLogoId } from '../../../constants';
import { isDarkColor, mixColor } from '../../../utils/color';
import { STUDIO_FONT_SC, STUDIO_FONT_TC } from '../../../utils/fonts';
import { CanvasLogoWatermark } from '../../LogoWatermark';
import StudioPageNumber from '../StudioPageNumber';

export interface StudioTokens {
  mode: 'clean' | 'vibrant';
  ink: string;
  muted: string;
  accent: string;
  onAccent: string;
  cardBg: string;
  surface: string;
  chipBg: string;
  hairline: string;
  pos: string;
  neg: string;
  shadow: string;
  radius: { sm: number; md: number; lg: number; xl: number; pill: number };
}

export interface LayoutContentProps {
  ink: string;
  mutedInk: string;
  accent: string;
  useLightInk: boolean;
  vibrant: boolean;
  tokens: StudioTokens;
}

interface LayoutFrameProps {
  width: number;
  height: number;
  format: string;
  themeColor?: string;
  profileName?: string;
  pageNumber?: number;
  pageCount?: number;
  watermarkLogo?: StudioWatermarkLogoId | null;
  showChubbDisclaimer?: boolean;
  pinContentTop?: boolean;
  children: React.ReactElement;
}

const FONT_STACK = `'DM Sans', '${STUDIO_FONT_TC}', '${STUDIO_FONT_SC}', 'Roboto', -apple-system, sans-serif`;

const buildTokens = (themeColor: string): StudioTokens => {
  const radius = { sm: 8, md: 12, lg: 16, xl: 20, pill: 9999 };

  // Clean / amber-style tokens: near-white canvas with a restrained accent.
  // Light theme colours are slightly deepened so badges stay readable with white text.
  const accent = isDarkColor(themeColor) ? themeColor : mixColor(themeColor, 'black', 0.28);

  return {
    mode: 'clean',
    ink: '#0a0a0a',
    muted: '#4b5563',
    accent,
    onAccent: '#ffffff',
    cardBg: '#ffffff',
    surface: mixColor(themeColor, 'white', 0.94),
    chipBg: mixColor(themeColor, 'white', 0.9),
    hairline: mixColor(themeColor, 'white', 0.82),
    pos: '#16a34a',
    neg: '#e11d48',
    shadow: '0 8px 24px rgba(15,23,42,0.07)',
    radius
  };
};

const LayoutFrame: React.FC<LayoutFrameProps> = ({
  width,
  height,
  format,
  themeColor = '#1456f0',
  profileName,
  pageNumber = 1,
  pageCount = 7,
  watermarkLogo,
  showChubbDisclaimer = false,
  pinContentTop = false,
  children
}) => {
  const tokens = buildTokens(themeColor);
  const isStory = format === 'story';
  const note = profileName && String(profileName).trim() ? String(profileName).trim() : '';
  const background = `linear-gradient(165deg, #ffffff 0%, ${mixColor(themeColor, 'white', 0.93)} 100%)`;

  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        color: tokens.ink,
        background,
        fontFamily: FONT_STACK,
        // Pin alignment so the page renders identically no matter what the host
        // is (the picker cover lives inside a MUI Button whose text-align:center
        // would otherwise centre the kicker/title/subtitle).
        textAlign: 'left'
      }}
    >
      {/* Decorative wash keyed to the theme colour */}
      <div
        style={{
          position: 'absolute',
          top: isStory ? '-14%' : '-18%',
          right: '-22%',
          width: '70%',
          height: '46%',
          borderRadius: '50%',
          background: mixColor(themeColor, 'white', 0.55),
          opacity: 0.45,
          filter: 'blur(46px)',
          pointerEvents: 'none'
        }}
      />

      <CanvasLogoWatermark logo={watermarkLogo} color="#000000" isStory={isStory} />
      <StudioPageNumber
        pageNumber={pageNumber}
        pageCount={pageCount}
        color={tokens.accent}
        isStory={isStory}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 12,
          padding: isStory ? '22px 30px 0' : '18px 28px 0',
          boxSizing: 'border-box',
          color: tokens.accent,
          fontSize: isStory ? 11 : 10,
          fontWeight: 700
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {note ? (
            <span
              style={{
                maxWidth: isStory ? 220 : 190,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: tokens.ink,
                letterSpacing: 0.2,
                textTransform: 'none'
              }}
            >
              {note}
            </span>
          ) : null}
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: isStory
            ? `28px 30px ${pinContentTop ? 136 : showChubbDisclaimer ? 52 : 26}px`
            : `22px 28px ${pinContentTop ? 80 : showChubbDisclaimer ? 46 : 20}px`,
          boxSizing: 'border-box',
          overflowWrap: 'anywhere'
        }}
      >
        {React.cloneElement(children as React.ReactElement<any>, {
          ink: tokens.ink,
          mutedInk: tokens.muted,
          accent: tokens.accent,
          useLightInk: false,
          vibrant: false,
          tokens
        })}
      </div>
    </div>
  );
};

export default LayoutFrame;
